import React, { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Grid,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import { useGetCampaignByIdQuery } from "../../store/apis/campaignApi";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import dayjs from "dayjs";
import { getPlatformDisplayName } from "../../utils/iconUtils";
import { useGetMergedCampaignInfoQuery } from "../../store/apis/internalCampaignsMergeApi";
import MergedCampaignInfo from "../../components/campaign/MergedCampaignInfo";
import ViewInternalCampaigns from "../../components/campaign/ViewInternalCampaigns";
import CampaignInternalCampaignsPanel from "../../components/campaign/CampaignInternalCampaignsPanel";
import { InsertionOrder } from "../../types/internalCampaignTypes";
import {
  resolveOrderKey,
  resolveOrderLabel,
  resolveOrderUniqueId,
  resolveOrderMargin,
  resolveSourceCampaignId,
} from "../../utils/insertionOrderUtils";
import type { InternalCampaign } from "../../types/internalCampaignTypes";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import PageContainer from "../../components/layout/PageContainer";
import { formatPercentValue } from "../../utils/numberFormat";
import { useGoBack } from "../../hooks/useGoBack";

const ViewCampaign: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const dispatch = useDispatch();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);
  const isAdmin = userDb?.role === "ADMIN";
  const theme = useTheme();

  // Fetch campaign data
  const {
    data: campaignResponse,
    isLoading: isLoadingCampaign,
    error: errorGetCampaign,
  } = useGetCampaignByIdQuery(
    { campaignId: campaignId || "" },
    { skip: !campaignId }
  );

  const { data: mergedCampaignInfo, error: errorGetMergedCampaignInfo } =
    useGetMergedCampaignInfoQuery(
      { campaignId: campaignId || "" },
      { skip: !campaignId }
    );

  // Handle navigation back to campaigns list
  const handleBack = () => {
    goBack("/campaigns");
  };

  const handleEdit = () => {
    navigate(`/campaigns/${campaignId}/edit`);
  };

  useEffect(() => {
    if (errorGetCampaign || errorGetMergedCampaignInfo) {
      dispatch(
        setToastMessage({
          message: t("campaigns.errorFetchingCampaign"),
          severity: "error",
        })
      );
    }
  }, [errorGetCampaign, errorGetMergedCampaignInfo, dispatch, t]);

  const insertionOrderMap = useMemo<Record<string, InsertionOrder>>(() => {
    const map: Record<string, InsertionOrder> = {};

    const selectedInsertionOrders =
      campaignResponse?.data?.dv360InsertionOrders?.selectedInsertionOrders ??
      [];
    const insertionOrders = (selectedInsertionOrders ||
      campaignResponse?.data?.dv360InsertionOrders?.insertionOrders ||
      campaignResponse?.data?.insertionOrders ||
      []) as InsertionOrder[];

    insertionOrders.forEach((order) => {
      const key =
        order?.insertionOrderId ||
        ("id" in order && order.id ? (order.id as string) : undefined);
      if (key) {
        map[key] = order;
      }
    });

    return map;
  }, [
    campaignResponse?.data?.dv360InsertionOrders?.selectedInsertionOrders,
    campaignResponse?.data?.dv360InsertionOrders?.insertionOrders,
    campaignResponse?.data?.insertionOrders,
  ]);

  useEffect(() => {
    if (!campaignResponse?.data) {
      return;
    }

    console.log(
      "[ViewCampaign] dv360InsertionOrders payload",
      campaignResponse.data.dv360InsertionOrders
    );
    console.log(
      "[ViewCampaign] legacy insertionOrders payload",
      campaignResponse.data.insertionOrders
    );
    console.log(
      "[ViewCampaign] insertionOrderMap keys",
      Object.keys(insertionOrderMap)
    );
  }, [campaignResponse?.data, insertionOrderMap]);

  const campaignData = campaignResponse?.data;

  if (!campaignData) {
    return (
      <Box sx={{ display: "flex" }}>
        <Box /* ...same layout styles as before... */>
          <PageBreadcrumbs />

          <PageContainer>
            <Paper /* ... */>
              <Typography>
                {t("campaigns.campaignNotFound", {
                  defaultValue: "Campaign not found",
                })}
              </Typography>
              <Button onClick={handleBack}>
                {t("common.backToCampaigns", {
                  defaultValue: "Back to campaigns",
                })}
              </Button>
            </Paper>
          </PageContainer>
        </Box>
      </Box>
    );
  }

  if (isLoadingCampaign) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <OrbLoader
          size="xlarge"
          label={t("common.loading", { defaultValue: "Cargando…" })}
        />
      </Box>
    );
  }

  const selectedInsertionOrders =
    campaignData.dv360InsertionOrders?.selectedInsertionOrders ?? [];

  const internalCampaigns = (campaignData.internalCampaigns ??
    []) as InternalCampaign[];
  const isDv360Parent = campaignData.platform === "DV360";
  const isDv360InternalPlatform = (platform?: string | null) =>
    Boolean(platform?.toUpperCase().includes("DV360"));
  const hasDv360Internals = internalCampaigns.some(
    (ic) =>
      isDv360InternalPlatform(ic.internalPlatform) ||
      isDv360InternalPlatform((ic as { platform?: string | null }).platform)
  );
  const isCtvParent = campaignData.platform === "CTV";
  const shouldShowParentInsertionOrders =
    isAdmin && (isDv360Parent || (isCtvParent && hasDv360Internals));
  const associatedInsertionOrders =
    campaignData.dv360InsertionOrders?.insertionOrders ?? [];

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          px: 4,
          py: 2,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "64px" : "250px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {userDb?.role !== "CLIENT" &&
              campaignData?.errorSync &&
              campaignData?.lastSyncAt &&
              dayjs().diff(dayjs(campaignData.lastSyncAt), "hour") <= 24 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tooltip
                    title={t("campaigns.syncErrorTooltip", {
                      error: campaignData.errorSync,
                    })}
                    arrow
                    placement="right"
                  >
                    <WarningAmberIcon color="error" sx={{ cursor: "help" }} />
                  </Tooltip>
                </Box>
              )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                variant="outlined"
                sx={{
                  borderRadius: 50,
                  textTransform: "none",
                  fontFamily: "Nunito",
                  borderColor: theme.palette.grey[200],
                  color: theme.palette.grey[700],
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: "transparent",
                  },
                }}
              >
                {t("common.back")}
              </Button>
              {isAdmin && (
                <Button
                  variant="contained"
                  onClick={handleEdit}
                  startIcon={<EditIcon />}
                  sx={{
                    borderRadius: 50,
                    textTransform: "none",
                    fontFamily: "Nunito",
                    fontWeight: 700,
                  }}
                >
                  {t("common.edit")}
                </Button>
              )}
            </Box>

            {campaignData && (
              <Grid container spacing={3}>
                {/* Campaign Details */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {t("campaigns.details")}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="textSecondary">
                            {t("campaigns.name")}
                          </Typography>
                          <Typography variant="body1">
                            {campaignData.displayName}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="textSecondary">
                            {t("campaigns.platform")}
                          </Typography>
                          <Typography variant="body1">
                            {getPlatformDisplayName(campaignData.platform)}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="textSecondary">
                            {t("campaigns.goal")}
                          </Typography>
                          <Typography variant="body1">
                            {campaignData.goal}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="textSecondary">
                            {t("campaigns.status")}
                          </Typography>
                          <Chip
                            label={t("campaignStatus.ACTIVE")}
                            size="small"
                            color={"success"}
                          />
                        </Box>
                      </Grid>

                      {shouldShowParentInsertionOrders &&
                        selectedInsertionOrders.length > 0 && (
                          <Grid item xs={12}>
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="subtitle2"
                                color="textSecondary"
                              >
                                {t("campaigns.selectedDv360InsertionOrders", {
                                  defaultValue:
                                    "Selected DV360 Insertion Orders",
                                })}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  mt: 1,
                                }}
                              >
                                {selectedInsertionOrders.map((order) => (
                                  <Chip
                                    key={order.insertionOrderId}
                                    label={
                                      order.displayName ||
                                      order.insertionOrderId
                                    }
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            </Box>
                          </Grid>
                        )}
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="textSecondary">
                            {t("campaigns.startDate")}
                          </Typography>
                          <Typography variant="body1">
                            {campaignData.startDate
                              ? dayjs(campaignData.startDate).format(
                                  "DD MMM YYYY"
                                )
                              : "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="textSecondary">
                            {t("campaigns.endDate")}
                          </Typography>
                          <Typography variant="body1">
                            {campaignData.endDate
                              ? dayjs(campaignData.endDate).format(
                                  "DD MMM YYYY"
                                )
                              : "-"}
                          </Typography>
                        </Box>
                      </Grid>

                      {campaignData.currency_enum && (
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="subtitle2"
                              color="textSecondary"
                            >
                              {t("campaigns.currency")}
                            </Typography>
                            <Typography variant="body1">
                              {campaignData.currency_enum}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>

                {/* Subcampaigns: metrics per subcampaign, each row opens its page */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t("campaigns.subcampaigns", {
                        defaultValue: "Subcampaigns",
                      })}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 2 }}
                    >
                      {t("campaigns.subcampaignsHint", {
                        defaultValue: "Click a subcampaign to open its detail.",
                      })}
                    </Typography>
                    <CampaignInternalCampaignsPanel
                      campaign={campaignData}
                      isAdmin={isAdmin}
                      isClient={false}
                      maxHeight={560}
                    />
                  </Paper>
                </Grid>

                {/* Merged Campaigns */}
                {mergedCampaignInfo && mergedCampaignInfo.length > 0 && (
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.grey[200]}`,
                      }}
                    >
                      <MergedCampaignInfo
                        mergedCampaigns={mergedCampaignInfo}
                        isAdmin={isAdmin}
                      />
                    </Paper>
                  </Grid>
                )}

                {/* Internal Campaigns */}
                <ViewInternalCampaigns
                  campaignResponse={campaignResponse}
                  isAdmin={isAdmin}
                  insertionOrderMap={insertionOrderMap}
                />

                {shouldShowParentInsertionOrders &&
                  campaignData.dv360InsertionOrders && (
                    <Grid item xs={12}>
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                        {t("campaigns.dv360AssociatedInsertionOrders", {
                          defaultValue: "DV360 Associated Insertion Orders",
                        })}
                      </Typography>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.grey[200]}`,
                          backgroundColor: theme.palette.grey[100],
                        }}
                      >
                        {associatedInsertionOrders.length ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {associatedInsertionOrders.map((order, index) => {
                              const orderKey = resolveOrderKey(
                                order as InsertionOrder,
                                index
                              );
                              const label = resolveOrderLabel(
                                order as InsertionOrder,
                                orderKey
                              );
                              const resolvedId = resolveOrderUniqueId(
                                order as InsertionOrder
                              );
                              const resolvedMargin = resolveOrderMargin(
                                order as InsertionOrder
                              );
                              const resolvedSourceCampaignId =
                                resolveSourceCampaignId(
                                  order as InsertionOrder
                                );

                              return (
                                <Paper
                                  key={orderKey}
                                  variant="outlined"
                                  sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    borderColor: theme.palette.grey[200],
                                    backgroundColor:
                                      theme.palette.background.default,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, mb: 0.5 }}
                                  >
                                    {label}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    {t("campaigns.dv360InsertionOrderId", {
                                      defaultValue: "Insertion Order ID",
                                    })}
                                    : {resolvedId || "-"}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    {t("campaigns.dv360InsertionOrderMargin", {
                                      defaultValue: "Margin",
                                    })}
                                    :
                                    {typeof resolvedMargin === "number"
                                      ? ` ${formatPercentValue(resolvedMargin)}`
                                      : " -"}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    {t(
                                      "campaigns.dv360InsertionOrderAdvertiser",
                                      {
                                        defaultValue: "Advertiser ID",
                                      }
                                    )}
                                    : {order.advertiserId || "-"}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    {t("campaigns.dv360InsertionOrderSource", {
                                      defaultValue: "Source Campaign ID",
                                    })}
                                    : {resolvedSourceCampaignId || "-"}
                                  </Typography>
                                </Paper>
                              );
                            })}
                          </Box>
                        ) : (
                          <Typography color="textSecondary">
                            {t("campaigns.dv360InsertionOrdersEmpty", {
                              defaultValue: "No DV360 insertion orders found.",
                            })}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  )}
              </Grid>
            )}
          </Box>
        </PageContainer>
      </Box>
    </Box>
  );
};

export default ViewCampaign;
